// R60: open a document/link in a new tab — PWA-safe. window.open() silently
// returns null in installed iOS PWAs (standalone display mode), which is
// exactly how cleaners run the app — the "proof of clean button not working"
// report. A synthetic anchor click works in every context (desktop browsers,
// mobile Safari, standalone PWAs), so it is the ONLY way the app should open
// external documents. Extra args are accepted and ignored so legacy
// window.open(url, '_blank', 'noopener') call sites convert mechanically.
export function openExternal(url: string, ..._ignored: any[]): void {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// R63: open a server-rendered HTML document that lives on an AUTHED /api
// route. Only the app's patched fetch() carries the Bearer token — a plain
// navigation (openExternal / anchor / window.open(url)) lands on a 401 JSON
// page in production; local dev hides this because loopback bypasses auth
// (the CEO's "proof of clean doesn't work" report, second occurrence).
// Mechanics: open a blank shell SYNCHRONOUSLY in the tap (iOS blocks popups
// after an await), fetch the HTML with auth, write it into the shell.
// Installed PWAs return null from window.open — there, fall back to a Blob
// URL via openExternal's synthetic anchor click.
// R64 (Alan does cleaning AND handyman work): field users switch their OWN
// view among the three FIELD roles — never office. userBaseRole (the profile
// role at login) gates the switcher: only cleaner/maintenance/inspector see
// it, so it can never elevate anyone. Admin "view as" is a separate,
// privileged mechanism in main.ts and is untouched.
const FIELD_ROLES = ['cleaner', 'maintenance', 'inspector'];
export function fieldViewSwitcherHtml(): string {
  try {
    const base = sessionStorage.getItem('userBaseRole') || sessionStorage.getItem('userRole') || '';
    if (!FIELD_ROLES.includes(base)) return '';
    const cur = sessionStorage.getItem('userRole') || base;
    const opts: Array<[string, string]> = [['cleaner', '🧹 Cleaning'], ['maintenance', '🔧 Maintenance'], ['inspector', '🔍 Inspections']];
    return `<div style="display:flex; gap:4px; margin:6px 0 2px;">${opts.map(([k, l]) =>
      `<button data-fieldswitch-to="${k}" title="Switch your view — your jobs for that role" style="flex:1; font-size:11px; font-weight:600; padding:7px 4px; border:1px solid ${k === cur ? 'var(--accent-border)' : 'var(--border)'}; border-radius:10px; background:${k === cur ? 'var(--accent-soft)' : 'var(--card)'}; color:${k === cur ? 'var(--accent)' : 'var(--text-muted)'}; cursor:pointer;">${l}</button>`).join('')}</div>`;
  } catch { return ''; }
}
export function bindFieldViewSwitcher(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-fieldswitch-to]').forEach(b => b.addEventListener('click', () => {
    try {
      const base = sessionStorage.getItem('userBaseRole') || sessionStorage.getItem('userRole') || '';
      if (!FIELD_ROLES.includes(base)) return;            // field users only — never an elevation path
      const to = b.dataset.fieldswitchTo!;
      if (!FIELD_ROLES.includes(to) || to === sessionStorage.getItem('userRole')) return;
      // fieldViewRole is the PERSISTED intent — boot re-stamps userRole every
      // load, so writing userRole directly would not survive the reload.
      sessionStorage.setItem('fieldViewRole', to);
      window.location.reload();                           // nav + page branches derive from the role at boot
    } catch { /* sessionStorage unavailable */ }
  }));
}

export async function openApiDoc(path: string, what: string): Promise<void> {
  const w = window.open('', '_blank');
  if (w) { try { w.document.write(`<title>${what}</title><p style="font-family:-apple-system,sans-serif;padding:24px;color:#475569;">Loading ${what}…</p>`); } catch (_) { /* still writable below */ } }
  try {
    const r = await fetch(path);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const html = await r.text();
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
    else {
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      openExternal(url);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  } catch (e: any) {
    if (w) { try { w.close(); } catch (_) { /* already gone */ } }
    alert(`${what} failed — ${e.message}`);
  }
}
